import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import {
  UserCheck,
  RefreshCw,
  ArrowRight,
  Calendar,
  CheckCircle2,
  Clock,
  AlertCircle,
  MessageCircle,
  Send,
  X,
  Paperclip
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog';
import { toast } from 'sonner';
import { useAuth } from 'contexts/AuthContext';
import { getRegistrationDetails } from 'services/newRegistrationService';
import { FullScreenLoading } from 'components/ui/loader';
import { useScreenPermission } from "hooks/useScreenPermission";
import { usePageHeader } from "contexts/PageHeaderContext";
import { FormatDate } from 'utils/commonutils';

interface TORegistrationDashboardPageProps {
  onNavigate: (page: string) => void;
}

interface Document {
  id: string;
  name: string;
  isRequired: boolean;
  uploadedUrl?: string;
  rejectionReason?: string;
  isRejected?: boolean;
  screens: string;
  status: string; // e.g. "tour_operator_registration"
}


interface Partner {
  partnerId: number;
  name: string;
  role: string;
  isMainOperator: boolean;
  permissionExpireDate?: string | null;
}
interface Registration {
  tourOperatorId: string;
  id: string;
  status: '';
  // partners: [];
  partners: Partner[];
  documents: Document[];
  rejectionReason?: string;
  createdAt?: string;
  updatedAt?: string;
}

export default function TORegistrationDashboardPage({ onNavigate }: TORegistrationDashboardPageProps) {
  const { user } = useAuth();
  const { setPageHeader } = usePageHeader();
  const [loading, setLoading] = useState(false);
  const [registration, setRegistration] = useState<Registration | null>(null);
  const rejectedDocsCount = registration?.documents?.filter(
    (doc) => doc?.screens === "tour_operator_registration")
    ?.filter((doc) => doc?.status === "REJECTED")
    ?.length ?? 0;

  const mainOwner = registration?.partners?.find((partner) => partner?.isMainOperator === true && partner?.role?.toUpperCase() === "OWNER");
  const permissionExpiryDate = FormatDate(String(mainOwner?.permissionExpireDate))


  // Calculate days remaining from today to renewal due date
  const renewalDueDate = new Date('2025-02-28');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  renewalDueDate.setHours(0, 0, 0, 0);
  const timeDiff = renewalDueDate.getTime() - today.getTime();
  const daysRemaining = Math.ceil(timeDiff / (1000 * 3600 * 24));

  const renewalStatus = {
    dueDate: '2025-02-28',
    daysRemaining: daysRemaining,
    status: 'renewal', // 'renewal' | 'due-soon' | 'expired'
    canRenew: true // Make button active
  };

  const {  screenName, icon: ScreenIcon} = useScreenPermission();

  useEffect(() => {
    setPageHeader({ 
      title: screenName,
      icon: <ScreenIcon className="h-6 w-6" />,
    });

    return () => setPageHeader(null);
  }, [screenName, ScreenIcon, setPageHeader]);


  const fetchRegistrationDetails = useCallback(async () => {
    if (!user?.tourOperatorId) return;
    try {
      setLoading(true);
      const res = await getRegistrationDetails(String(user?.tourOperatorId));
      if (!res) {
        toast.error("No registration details found");
        return;
      }
      setRegistration(res);
    } catch (error) {
      toast.error("Failed to fetch registration details");
    } finally {
      setLoading(false);
    }
  }, [user?.tourOperatorId]);

  useEffect(() => {
    fetchRegistrationDetails();
  }, [fetchRegistrationDetails, user?.tourOperatorId]);


  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'Approved':
        return 'bg-green-500';
      case 'Submitted':
        return 'bg-blue-500';
      case 'Pending':
        return 'bg-orange-500';
      case 'Rejected':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusIcon = (status?: string) => {
    switch (status) {
      case 'Approved':
        return <CheckCircle2 className="w-5 h-5" />;
      case 'Pending':
        return <Clock className="w-5 h-5" />;
      case 'Rejected':
        return <AlertCircle className="w-5 h-5" />;
      default:
        return <Clock className="w-5 h-5" />;
    }
  };

  const getStatusText = (status?: string) => {
    switch (status) {
      case 'Approved':
        return 'Approved';
      case 'Submitted':
        return 'Submitted';
      case 'Pending':
        return 'Under Review';
      case 'Rejected':
        return 'Rejected';
      default:
        return 'Pending';
    }
  };


  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [chatMessages, setChatMessages] = useState<Array<{
    id: string;
    sender: 'user' | 'admin';
    text: string;
    time: string;
  }>>([
    {
      id: '1',
      sender: 'admin',
      text: 'Hello! How can we help you today?',
      time: '10:30 AM'
    }
  ]);

  const handleSendMessage = () => {
    if (!message.trim()) return;

    const newMessage = {
      id: Date.now().toString(),
      sender: 'user' as const,
      text: message,
      time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    };

    setChatMessages([...chatMessages, newMessage]);
    setMessage('');
    toast.success('Message sent!');

    // Simulate admin response
    setTimeout(() => {
      const adminResponse = {
        id: (Date.now() + 1).toString(),
        sender: 'admin' as const,
        text: 'Thank you for your message. Our team will review and respond shortly.',
        time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
      };
      setChatMessages(prev => [...prev, adminResponse]);
    }, 1500);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      {loading && <FullScreenLoading />}

      {/* Summary Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" >

        {/* Registration Review Card */}
        <Card className="group hover:shadow-lg transition-all duration-300 cursor-pointer border-sky-200 overflow-hidden bg-gradient-to-br from-white to-sky-50" style={{ marginTop: "0px" }}
          // onClick={() => onNavigate('to-registration-review')}>
          onClick={() => onNavigate(`/my-registration/view/${user?.tourOperatorId}`)}>
          <CardContent className="p-6">
            {/* Icon and Badge Row */}
            <div className="flex items-start justify-between mb-4">
              <div className="w-14 h-14 rounded-xl flex items-center justify-center shadow-md" style={{ backgroundColor: '#5B9BD5' }}>
                <UserCheck className="w-7 h-7 text-white" />
              </div>
              <Badge
                className={`${getStatusColor(registration?.status)} text-white px-3 py-1 flex items-center gap-2`}
              >
                {getStatusIcon(registration?.status)}
                {getStatusText(registration?.status)}
              </Badge>
            </div>

            {/* Title */}
            <h2 className="text-xl font-bold text-gray-900 mb-1">
              Registration Review
            </h2>
            <p className="text-gray-600 text-sm mb-4">
              View and manage your tour operator registration details
            </p>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-sky-50 rounded-lg p-3 border border-sky-100">
                <div className="text-sm font-medium mb-1" style={{ color: '#5B9BD5' }}>Submitted</div>
                <div className="text-gray-900 font-semibold text-sm">
                  {registration?.createdAt
                    ? new Date(registration.createdAt).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })
                    : "N/A"}
                </div>
              </div>

              <div className="bg-sky-50 rounded-lg p-3 border border-sky-100">
                <div className="text-sm font-medium mb-1" style={{ color: '#5B9BD5' }}>Partners</div>
                {/* <div className="text-gray-900 font-semibold text-sm">{registration?.partners?.length || 0}</div> */}
                <div className="text-gray-900 font-semibold text-sm">
                  {registration?.partners?.filter(p => !p?.isMainOperator).length || 0}
                </div>
              </div>
            </div>

            {/* Action Required Alert */}
            {rejectedDocsCount > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-red-900 font-semibold text-xs mb-0.5">Action Required</div>
                    <div className="text-red-700 text-xs">
                      {rejectedDocsCount} document(s) rejected. Please re-upload.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {permissionExpiryDate !== null && (
              <div className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs">
                <span className="flex items-center gap-2 font-medium text-amber-800">
                  <Calendar className="h-4 w-4 shrink-0" />
                  Permission expires
                </span>
                <span className="font-semibold text-amber-950">{permissionExpiryDate}</span>
              </div>
            )}

            {/* Action Button */}
            <Button
              className="w-full text-white shadow-md hover:shadow-lg transition-all"
              style={{ backgroundColor: '#5B9BD5' }}
            >
              <span>View Registration</span>
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>

            {/* Last Updated */}
            <div className="mt-3 flex items-center justify-center gap-2 text-xs text-gray-500">
              <Clock className="w-3 h-3" />
              <span>Last updated:{" "}
                {registration?.updatedAt
                  ? new Date(registration.updatedAt).toLocaleDateString()
                  : registration?.createdAt
                    ? new Date(registration.createdAt).toLocaleDateString()
                    : "N/A"}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Renewal Review Card */}
        <Card className="hidden group hover:shadow-lg transition-all duration-300 cursor-pointer border-sky-200 overflow-hidden bg-gradient-to-br from-white to-sky-50"
          onClick={() => onNavigate('to-renewal-review')}>
          <CardContent className="p-6">
            {/* Icon and Badge Row */}
            <div className="flex items-start justify-between mb-4">
              <div className="w-14 h-14 rounded-xl flex items-center justify-center shadow-md" style={{ backgroundColor: '#5B9BD5' }}>
                <RefreshCw className="w-7 h-7 text-white" />
              </div>
              <Badge className={`${renewalStatus.daysRemaining <= 30
                ? 'bg-red-500'
                : renewalStatus.daysRemaining <= 90
                  ? 'bg-orange-500'
                  : 'bg-green-500'
                } text-white px-2 py-0.5 text-xs`}>
                {renewalStatus.daysRemaining <= 30
                  ? 'Due Soon'
                  : renewalStatus.daysRemaining <= 90
                    ? 'Renewal Approaching'
                    : 'Renewal'}
              </Badge>
            </div>

            {/* Title */}
            <h2 className="text-xl font-bold text-gray-900 mb-1">
              Renewal Review
            </h2>
            <p className="text-gray-600 text-sm mb-4">
              Manage your registration renewal and permissions
            </p>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-sky-50 rounded-lg p-3 border border-sky-100">
                <div className="text-sm font-medium mb-1" style={{ color: '#5B9BD5' }}>Renewal Due</div>
                <div className="text-gray-900 font-semibold text-sm">
                  {new Date(renewalStatus.dueDate).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </div>
              </div>

              <div className="bg-sky-50 rounded-lg p-3 border border-sky-100">
                <div className="text-sm font-medium mb-1" style={{ color: '#5B9BD5' }}>Days Left</div>
                <div className="text-gray-900 font-semibold text-sm">{renewalStatus.daysRemaining}</div>
              </div>
            </div>

            {/* Info Alert */}
            <div className="bg-sky-50 border border-sky-100 rounded-lg p-3 mb-4">
              <div className="flex items-start gap-2">
                <Calendar className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#5B9BD5' }} />
                <div>
                  <div className="text-gray-900 font-semibold text-xs mb-0.5">Renewal Information</div>
                  <div className="text-gray-700 text-xs">
                    Your registration is valid until {new Date(renewalStatus.dueDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}.
                  </div>
                </div>
              </div>
            </div>

            {/* Action Button */}
            <Button
              className="w-full text-white shadow-md hover:shadow-lg transition-all"
              style={{ backgroundColor: '#5B9BD5' }}
            >
              <span>View Renewal Status</span>
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>

            {/* Coming Soon Badge - Removed as button is now active */}
          </CardContent>
        </Card>

      </div>

      {/* Quick Actions Footer */}
      <Card className="hidden border-sky-200 bg-gradient-to-br from-white to-sky-50">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-900 text-sm mb-0.5">Need Help?</h3>
              <p className="text-xs text-gray-600">Contact our support team for assistance with your registration</p>
            </div>
            <Button
              variant="outline"
              className="border-sky-200 hover:bg-sky-50"
              style={{ color: '#5B9BD5' }}
              onClick={() => setIsDialogOpen(true)}
            >
              Contact Support
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Contact Support Chat Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[500px] p-0 gap-0 overflow-hidden">
          <DialogHeader className="sr-only">
            <DialogTitle>Support Chat</DialogTitle>
            <DialogDescription>Chat with our support team</DialogDescription>
          </DialogHeader>

          {/* Chat Header */}
          <div className="p-4 border-b" style={{ backgroundColor: '#5B9BD5' }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
                  <MessageCircle className="w-5 h-5" style={{ color: '#5B9BD5' }} />
                </div>
                <div>
                  <h3 className="font-semibold text-white">Support Chat</h3>
                  <p className="text-xs text-white/80">We typically reply within minutes</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/20 rounded-full"
                onClick={() => setIsDialogOpen(false)}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Chat Messages */}
          <div className="flex-1 p-4 space-y-4 max-h-[400px] overflow-y-auto bg-gray-50">
            {chatMessages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[75%] rounded-2xl px-4 py-2 ${msg.sender === 'user'
                    ? 'text-white'
                    : 'bg-white border border-gray-200'
                    }`}
                  style={msg.sender === 'user' ? { backgroundColor: '#5B9BD5' } : {}}
                >
                  <p className="text-sm">{msg.text}</p>
                  <p className={`text-xs mt-1 ${msg.sender === 'user' ? 'text-white/70' : 'text-gray-500'}`}>
                    {msg.time}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Message Input */}
          <div className="p-4 border-t bg-white">
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  placeholder="Type your message..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-sky-200"
                  rows={2}
                />
              </div>
              <div className="flex flex-col gap-2">
                <Button
                  size="icon"
                  variant="outline"
                  className="rounded-xl border-gray-300 hover:bg-gray-50"
                  title="Attach file"
                >
                  <Paperclip className="w-4 h-4 text-gray-600" />
                </Button>
                <Button
                  size="icon"
                  className="rounded-xl text-white shadow-md"
                  style={{ backgroundColor: '#5B9BD5' }}
                  onClick={handleSendMessage}
                  disabled={!message.trim()}
                  title="Send message"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
